import React, { useRef, useState, useMemo, useCallback, useEffect } from "react";
import { EditOutlined, DeleteOutlined, PlusOutlined, SettingOutlined } from "@ant-design/icons";
import { Button, Form, Modal, Row, Col, InputNumber, Input } from "antd";
import { connect } from "dva";
import { getUrlParams, numberToPrice, formatPrice } from "@/utils/utils";
import AlertWithBorder from "@/components/AlertWithBorder";
import MoreMenu from "@/components/MoreMenu";
import TableWithPagination from "@/components/TableWithPagination";
import {
  PricingValueTypeEnum,
  DealerStrategiesEnum,
  fetchDealerStrategyByType,
  dealerStrategyInitialParamsByType,
} from "./constants";

const PricingBands = ({ configPerm, inherited, strategy, dealerPricingBandsSettings, loadingSettings, dispatch }) => {
  const initialParams = useMemo(() => {
    return dealerStrategyInitialParamsByType[DealerStrategiesEnum.PricingBands];
  }, []);

  const formRef = useRef(null);
  const settingsFormRef = useRef(null);
  const filledSettingsRef = useRef(null);
  const lastSortingConfigFetched = useRef(initialParams);

  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [editingRow, setEditingRow] = useState(null);
  const [deletingRow, setDeletingRow] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [loadingSubmitRow, setLoadingSubmitRow] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [distanceLower, setDistanceLower] = useState(null);
  const [distanceUpper, setDistanceUpper] = useState(null);

  const disabled = useMemo(() => {
    return !configPerm["PricingService.PricingSettings.Update"] || inherited;
  }, [configPerm, inherited]);

  const { dataSource, totalItems, defaultScenario } = useMemo(() => {
    if (!strategy) {
      return {
        dataSource: [],
        totalItems: 0,
        defaultScenario: false,
      };
    }

    return {
      dataSource: strategy.items,
      totalItems: strategy.totalCount || 0,
      defaultScenario: strategy.valueType === PricingValueTypeEnum.Default,
    };
  }, [strategy]);

  const invalidDistances = useMemo(() => {
    if (distanceLower === null || distanceUpper === null) {
      return false;
    }

    return parseInt(distanceLower) >= parseInt(distanceUpper);
  }, [distanceLower, distanceUpper]);

  const resetFormFields = useCallback(() => {
    formRef.current.resetFields();
    setEditingRow(null);
  }, []);

  const handleCreateCancel = useCallback(() => {
    resetFormFields();
    setModalOpen(false);
    setDistanceLower(null);
    setDistanceUpper(null);
  }, [resetFormFields]);

  const handleCreateConfirm = useCallback(() => {
    formRef.current.validateFields().then(response => {
      let type;
      let payload = {};

      const { price } = response;
      const distanceLower = parseInt(response.distanceLower);
      const distanceUpper = parseInt(response.distanceUpper);

      if (editingRow === null) {
        // new row
        type = "pricingStrategies/dealerPricingBandsAddRow";
        payload = {
          referenceId: strategy.referenceId,
          distanceLower,
          distanceUpper,
          price,
          page,
          pageSize,
        };
      } else {
        // edit row
        type = "pricingStrategies/dealerPricingBandsUpdateRow";
        payload = {
          distanceLower,
          distanceUpper,
          price,
          id: editingRow.id,
          referenceId: strategy.referenceId,
          concurrencyStamp: editingRow.concurrencyStamp,
          page,
          pageSize,
        };
      }

      setLoadingSubmitRow(true);
      dispatch({
        type,
        ...payload,
      }).then(success => {
        setLoadingSubmitRow(false);

        if (success) {
          setModalOpen(false);
          setEditingRow(null);
          setDistanceLower(null);
          setDistanceUpper(null);
        }
      });
    });
  }, [strategy, editingRow, page, pageSize]);

  const onAddRow = useCallback(() => {
    setEditingRow(null);
    setModalOpen(true);
  }, []);

  const handleSettingsConfirm = useCallback(() => {
    settingsFormRef.current.validateFields().then(result => {
      dispatch({
        type: "pricingStrategies/dealerPricingBandsSetSettings",
        id: dealerPricingBandsSettings.id,
        concurrencyStamp: dealerPricingBandsSettings.concurrencyStamp,
        threshold: result.threshold,
        pricePerMile: result.pricePerMile,
        flatRate: result.flatRate,
      }).then(success => {
        if (success) {
          filledSettingsRef.current = null;
          setSettingsModalOpen(false);
        }
      });
    });
  }, [dealerPricingBandsSettings]);

  const handleDelete = useCallback(() => {
    let newPage = page;
    if (strategy.totalCount - 1 === page * pageSize && newPage > 0) {
      newPage--;

      lastSortingConfigFetched.current = {
        page: newPage,
        pageSize,
      };

      setPage(newPage);
    }

    setLoadingDelete(true);
    dispatch({
      type: "pricingStrategies/dealerPricingBandsDeleteRow",
      referenceId: strategy.referenceId,
      rowId: deletingRow.id,
      page: newPage,
      pageSize,
    }).finally(() => {
      setLoadingDelete(false);
      setDeletingRow(null);
    });
  }, [deletingRow, strategy.referenceId, strategy.totalCount, page, pageSize]);

  const handleCancelDelete = useCallback(() => {
    setDeletingRow(null);
  }, []);

  const handleSettingsCancel = useCallback(() => {
    settingsFormRef.current.resetFields();
    setSettingsModalOpen(false);
    filledSettingsRef.current = null;
  }, []);

  const onMenuItemClick = useCallback((key, row) => {
    switch (key) {
      case "edit": {
        setEditingRow(row);
        setModalOpen(true);
        break;
      }

      case "delete":
        setDeletingRow(row);
        break;
    }
  }, []);

  useEffect(() => {
    if (editingRow === null) {
      return;
    }

    formRef.current.setFieldsValue({
      distanceLower: editingRow.distanceLower,
      distanceUpper: editingRow.distanceUpper,
      price: editingRow.price,
    });
  }, [editingRow]);

  useEffect(() => {
    const { page: lastCheckedPage, pageSize: lastCheckedPageSize } = lastSortingConfigFetched.current;

    if (lastCheckedPage === page && lastCheckedPageSize === pageSize) {
      return;
    }

    lastSortingConfigFetched.current = {
      page,
      pageSize,
    };

    dispatch({
      type: fetchDealerStrategyByType[DealerStrategiesEnum.PricingBands],
      referenceId: strategy.referenceId,
      page,
      pageSize,
    });
  }, [page, pageSize, strategy.referenceId]);

  useEffect(() => {
    dispatch({
      type: "pricingStrategies/dealerPricingBandsGetSettings",
      referenceId: getUrlParams("referenceId"),
    });
  }, []);

  useEffect(() => {
    if (dealerPricingBandsSettings === null || filledSettingsRef.current !== null || !settingsModalOpen) {
      return;
    }

    const { flatRate, pricePerMile, threshold } = dealerPricingBandsSettings;

    filledSettingsRef.current = {
      flatRate,
      pricePerMile,
      threshold,
    };

    settingsFormRef.current.setFieldsValue({
      flatRate,
      pricePerMile,
      threshold,
    });
  }, [dealerPricingBandsSettings, settingsModalOpen]);

  return (
    <div>
      <div
        style={{
          display: inherited ? "none" : "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          gap: "8px",
        }}
      >
        <Button
          type="primary"
          icon={<SettingOutlined />}
          disabled={disabled}
          onClick={() => setSettingsModalOpen(true)}
        >
          Settings
        </Button>
        {!disabled && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{
              borderRadius: 10,
              color: "#fff",
            }}
            onClick={onAddRow}
          />
        )}
      </div>
      <TableWithPagination
        page={page}
        pageSize={pageSize}
        totalItems={totalItems}
        dataSource={dataSource}
        columns={[
          {
            title: "Lower Distance",
            dataIndex: "distanceLower",
          },
          {
            title: "Upper Distance",
            dataIndex: "distanceUpper",
          },
          {
            title: "Price",
            dataIndex: "price",
            render: price => {
              return <span>{formatPrice(price)}</span>;
            },
          },
          !inherited &&
            !disabled &&
            !defaultScenario && {
              title: "",
              dataIndex: "actions",
              render: (text, row) => {
                return (
                  <MoreMenu
                    onItemClick={key => onMenuItemClick(key, row)}
                    items={[
                      {
                        key: "edit",
                        icon: <EditOutlined />,
                        label: "Edit",
                        authority: "PricingService.PricingSettings.Update",
                      },
                      {
                        key: "delete",
                        icon: <DeleteOutlined />,
                        label: "Delete",
                        authority: "PricingService.PricingSettings.Update",
                      },
                    ]}
                  />
                );
              },
            },
        ]}
        onPageChange={page => setPage(page)}
        onPageSizeChange={pageSize => setPageSize(pageSize)}
      />

      <Modal
        title={`${editingRow === null ? "New" : "Edit"} Pricing Band`}
        okText="Save changes"
        visible={modalOpen}
        okButtonProps={{
          disabled: invalidDistances,
        }}
        onOk={handleCreateConfirm}
        onCancel={handleCreateCancel}
        confirmLoading={loadingSubmitRow}
        afterClose={() => {
          formRef.current.resetFields();
        }}
      >
        {defaultScenario && editingRow === null && (
          <AlertWithBorder type="info" message="Configuring a new pricing band will delete the current ones." />
        )}

        {invalidDistances && (
          <AlertWithBorder type="warning" message="Lower distance must be smaller than upper distance." />
        )}

        <Form ref={formRef} layout="vertical">
          <Row gutter={16}>
            <Col lg={12}>
              <Form.Item name="distanceLower" label="Lower Distance" rules={[{ required: true }]}>
                <Input onChange={event => setDistanceLower(event.target.value)} />
              </Form.Item>
            </Col>
            <Col lg={12}>
              <Form.Item name="distanceUpper" label="Upper Distance" rules={[{ required: true }]}>
                <Input onChange={event => setDistanceUpper(event.target.value)} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col lg={24}>
              <Form.Item name="price" label="Price" rules={[{ required: true }]}>
                <InputNumber
                  style={{ width: "100%" }}
                  formatter={value => numberToPrice(value, "$ ")}
                  parser={value => value.replace(/\$\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Delete"
        okText="Delete"
        visible={deletingRow !== null}
        onOk={handleDelete}
        onCancel={handleCancelDelete}
        confirmLoading={loadingDelete}
      >
        <span>Are you sure you want to delete this row?</span>
      </Modal>

      <Modal
        title="Edit settings"
        okText="Save changes"
        visible={settingsModalOpen}
        onOk={handleSettingsConfirm}
        onCancel={handleSettingsCancel}
        confirmLoading={loadingSettings}
        afterClose={() => {
          settingsFormRef.current.resetFields();
        }}
      >
        <Form layout="vertical" ref={settingsFormRef}>
          <Row gutter={16}>
            <Col lg={12}>
              <Form.Item label="Flat rate" name="flatRate" rules={[{ required: true }]}>
                <InputNumber />
              </Form.Item>
            </Col>
            <Col lg={12}>
              <Form.Item label="Price per mile" name="pricePerMile" rules={[{ required: true }]}>
                <InputNumber
                  formatter={value => numberToPrice(value, "$ ")}
                  parser={value => value.replace(/\$\s?|(,*)/g, "")}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col lg={12}>
              <Form.Item label="Threshold in miles" name="threshold" rules={[{ required: true }]}>
                <InputNumber />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default connect(({ loading, configs, pricingStrategies }) => {
  return {
    configPerm: configs.configPerm,
    dealerPricingBandsSettings: pricingStrategies.dealerPricingBandsSettings,
    loadingSettings: loading.effects["pricingStrategies/dealerPricingBandsSetSettings"],
  };
})(PricingBands);
